import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import HttpService from "../services/http";
import { queryKeys } from "../constants/queryKeys";
import { queryClient } from "../providers/QueryProvider";
import { entityCache } from "./entity";

const PREFERENCE_KEY = "communication_pref";

type CommunicationChannel = "sms" | "email" | "whatsapp";

type CommunicationPrefs = {
  sms?: {
    blocked?: boolean;
  };
  email?: {
    blocked?: boolean;
  };
  whatsapp?: {
    blocked?: boolean;
  };
};

export const useCommunicationPreferences = (
  customer_identification: string,
  entityIdentification: string,
  userIdentification: string
) => {
  const [localPrefs, setLocalPrefs] = useState<CommunicationPrefs>({});
  const latestPrefsRef = useRef<CommunicationPrefs>({});

  const queryKey = [
    queryKeys.entityPreferences,
    customer_identification,
    entityIdentification,
    userIdentification,
    PREFERENCE_KEY,
  ];

  const { data, isLoading } = useQuery<any, AxiosError>(
    queryKey,
    async () => {
      const cacheKey = `user_data_${userIdentification}_${PREFERENCE_KEY}`;
      const cached = entityCache.get(cacheKey);
      if (cached) return cached;

      const response = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${userIdentification}/data/${PREFERENCE_KEY}`
      );

      const result = response?.data?.element;
      entityCache.setWithLongTTL(cacheKey, result);
      return result;
    },
    {
      enabled:
        !!customer_identification &&
        !!entityIdentification &&
        !!userIdentification,
      retry: false,
      onError: () => {},
    }
  );

  const serverPrefs: CommunicationPrefs =
    data?.user_data?.communication_pref || {};

  useEffect(() => {
    setLocalPrefs(serverPrefs);
    latestPrefsRef.current = serverPrefs;
  }, [JSON.stringify(serverPrefs)]);

  const { mutate, isLoading: isUpdating } = useMutation<
    any,
    AxiosError<any>,
    CommunicationPrefs
  >(
    (updatedComm: CommunicationPrefs) =>
      HttpService.patch(
        `/${customer_identification}/${entityIdentification}/user/${userIdentification}/data/${PREFERENCE_KEY}`,
        { user_data: updatedComm }
      ),
    {
      onSuccess: () => {
        const cacheKey = `user_data_${userIdentification}_${PREFERENCE_KEY}`;
        entityCache.delete(cacheKey);

        queryClient.invalidateQueries(queryKey);
        queryClient.invalidateQueries(["user_data", customer_identification]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );

  const isSmsBlocked = localPrefs.sms?.blocked === true;
  const isEmailBlocked = localPrefs.email?.blocked === true;
  const isWhatsappBlocked = localPrefs.whatsapp?.blocked === true;

  const toggle = useCallback(
    (channel: CommunicationChannel, blocked: boolean) => {
      const updatedComm = { ...latestPrefsRef.current };
      updatedComm[channel] = { ...latestPrefsRef.current[channel], blocked };

      const previousPrefs = { ...latestPrefsRef.current };

      setLocalPrefs(updatedComm);
      latestPrefsRef.current = updatedComm;

      mutate(updatedComm, {
        onError: () => {
          setLocalPrefs(previousPrefs);
          latestPrefsRef.current = previousPrefs;
        },
      });
    },
    [mutate]
  );

  return {
    isSmsBlocked,
    isEmailBlocked,
    isWhatsappBlocked,
    toggle,
    isLoading,
    isUpdating,
  };
};
